import { ChangeDetectorRef, Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { DailyReportService } from '../../../../daily-report.service';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { dailyReport, projectDetails } from '../../../../../../../../core/projects.selectors';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import { ProjectsService } from '../../../../../../../../core/projects.service';
import { AppService } from '../../../../../../../../../../shared/services';
import * as $ from 'jquery';
import { DeleteVideoFromMuxDbRequest } from '../../../../../../../../core/projects.actions';
import { MatDialog } from '@angular/material/dialog';
import { carryLinkRemovals, clearLocationControls, linkRawValue, patchPickedLocation } from '../../../../../../../../../../shared/helpers/design-context';
import { PickTarget } from '../../../../../../../../../../shared/helpers/design-context-pick';

@Component({
    selector: 'app-delivery-details',
    templateUrl: './delivery-details.component.html',
    styleUrls: ['../../input-component-styles.scss', './delivery-details.component.scss'],
    standalone: false
})
export class DeliveryDetailsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() data: any;
  // Hidden when an outer header already labels the panel (e.g. the map drilldown
  // sidenav, which shows its own "Delivery" header) to avoid a duplicate title.
  @Input() showHeading = true;
  form = new FormGroup({});
  materialForm = new FormGroup({
    materials: new FormControl([]),
  });
  materialOptions = [];
  customFields: any[] = [];
  selectedMaterialId: string;
  showHeaderDetails = true;
  project: any;
  selectedBidItem: any;
  linesToDelete: any[] = [];
  formSubscription: Subscription = new Subscription();
  selectedMediaFiles = [];
  videoToBeDeletedFromMuxDb = [];
  private readonly pickTarget = new PickTarget();
  private readonly dialog = inject(MatDialog);
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private commonService: CommonService,
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    public appService: AppService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.project = data;
        this.customFields = data.delivery_tracking_fields;

        if (this.data) {
          this.createForm();
        }

        if (this.data?.subType === 'bidItemWithMaterials') {
          this.selectedBidItem = data.bid_items.find(o => o.id === this.data.initialState.bid_item_id);
          this.updateMaterialsDropdown();
        }
      });

    this.materialForm.controls.materials.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe((data: any) => {
      const allMaterialIds = this.data.materials.map(o => o.initialState.material.id);
      if (data.length > this.data.materials.length) {
        const extraElement = data.filter(x => !allMaterialIds.includes(x))[0];
        this.addMaterialToBidItem(extraElement);
      }
    });

    this.commonService.uploadedFilesInfo.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      const items = Array.isArray(data) ? data : [data];
      const formGroup = this.getFormGroup(this.form, this.selectedMaterialId);

      items.forEach(eachItem => {
        if (eachItem?.['fileType'] === 'picture' || eachItem?.['fileType'] === 'document') {
          let pictures = formGroup?.get('pictures')?.value || '[]';
          pictures = JSON.parse(pictures);
          pictures.push(eachItem);
          formGroup?.get('pictures')?.setValue(JSON.stringify(pictures));
        }

        if (eachItem?.['fileType'] === 'video') {
          let videos = formGroup?.get('videos')?.value || '[]';
          videos = JSON.parse(videos);
          videos.push(eachItem);
          formGroup?.get('videos')?.setValue(JSON.stringify(videos));
        }
      });

      const tempPictureData = this.getSelectedPictures() ?? [];
      const tempVideosData = this.getSelectedVideos() ?? [];
      const allNewItems = [...tempPictureData, ...tempVideosData];

      allNewItems.forEach(item => {
        const existingItem = this.selectedMediaFiles.find(ei => ei.id === item.id);

        if (!existingItem) {
          this.selectedMediaFiles.push(item);
        }
      });

      this.selectedMediaFiles = [...this.selectedMediaFiles];
      this.cdRef.detectChanges();
    });

    this.dailyReportService.addComment.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      if (this.form) {
        const tempControl = this.getFormGroup(this.form, this.selectedMaterialId).get('comment');

        if (!tempControl) {
          this.getFormGroup(this.form, this.selectedMaterialId)?.addControl('comment', new FormControl(null));
        }
      }
    });

    this.dailyReportService.triggerSave.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.dailyReportService.formatAndSaveDelivery(linkRawValue(this.form), this.data, this.linesToDelete);
      if (this.videoToBeDeletedFromMuxDb.length) {
        this.videoToBeDeletedFromMuxDb.forEach(videoId => {
          this.store.dispatch(DeleteVideoFromMuxDbRequest({ payload: videoId }));
        });
      }
    });

    this.dailyReportService.triggerDelete.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.store
        .select(dailyReport)
        .subscribe(report => {
          const { material_deliveries } = report;

          const deliveryItem = this.data;

          const elementIds = [];

          material_deliveries.forEach(delivery => {
            if (deliveryItem.subType === 'bidItemWithMaterials') {
              if (
                delivery.bid_item !== null &&
                delivery.material !== null &&
                delivery.received !== null &&
                delivery.used === null &&
                delivery.quantity === 0
              ) {
                elementIds.push(delivery.id);
              }

              if (
                delivery.bid_item !== null &&
                (delivery.material !== null || true) &&
                delivery.received === null &&
                delivery.used === null &&
                delivery.quantity === 0
              ) {
                elementIds.push(delivery.id);
              }
            }

            if (deliveryItem.subType === 'onlyBidItem') {
              if (
                delivery.bid_item !== null &&
                delivery.material === null &&
                delivery.received === null &&
                delivery.used === null &&
                delivery.quantity > 0
              ) {
                elementIds.push(delivery.id);
              }
            }

            if (deliveryItem.subType === 'onlyMaterial') {
              if (
                delivery.bid_item === null &&
                delivery.material !== null &&
                delivery.received !== null &&
                delivery.used === null &&
                delivery.quantity === 0
              ) {
                elementIds.push(delivery.id);
              }
            }
          });

          this.dailyReportService.delete.next({
            type: 'delivery',
            id: elementIds,
          });
        })
        .unsubscribe();
    });

    // Pickers (card, toolbar, station dialog) are modal: the material selected when one opens is the one its pick lands on.
    this.dialog.afterOpened.pipe(takeUntil(this.onDestroy)).subscribe(ref => this.holdPickTarget(ref.afterClosed()));

    this.dailyReportService.selectedLocation.pipe(takeUntil(this.onDestroy)).subscribe(location => {
      if (this.form) {
        this.applyPickedLocation(location);
      }
    });
  }

  /** Remember the material selected as a picker opens until it closes (Review Focus 5). */
  holdPickTarget(closed: Observable<unknown>) {
    this.pickTarget.hold(this.selectedMaterialId, closed);
  }

  /** Only the keys the picker sent are patched: an absent link key leaves the line's link. */
  applyPickedLocation(location: unknown) {
    const lineItemId = this.pickTarget.resolve(this.selectedMaterialId);
    patchPickedLocation(this.getFormGroup(this.form, lineItemId), location);
  }

  ngOnChanges() {
    this.linesToDelete = [];
    if (this.data) {
      this.createForm();
    }
  }

  createForm() {
    const before = this.form;
    const values = before.getRawValue();
    this.form = new FormGroup({});

    const parentFormBuilder = {};

    parentFormBuilder[this.data.id] = this.createLineItemForm(this.fb.group({}), this.data);

    if (this.data?.materials?.length > 0) {
      this.data?.materials.map(material => {
        parentFormBuilder[material.id] = this.createLineItemForm(this.fb.group({}), material);
      });
    }

    this.form = new FormGroup(parentFormBuilder);

    this.form.patchValue(values, { emitEvent: false });
    // patchValue carries no dirtiness: keep a link removed in this session removed (R54)
    carryLinkRemovals(before, this.form);

    this.selectedMaterialId = this.data.id;

    const tempPictureData = this.getSelectedPictures() ?? [];
    const tempVideosData = this.getSelectedVideos() ?? [];
    this.selectedMediaFiles = [...tempPictureData, ...tempVideosData];

    this.formSubscription = this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      if (this.form.valid) {
        this.projectsService.isAnyFormDirty.next(true);
      } else {
        this.projectsService.isAnyFormDirty.next(false);
      }
    });
  }

  createLineItemForm(form, data) {
    if (data.subType !== 'bidItemWithMaterials') {
      form.addControl('quantity', new FormControl(data.quantity, [Validators.required]));
    }

    if (data.comment) {
      form.addControl('comment', new FormControl(data.comment));
    }

    if (data.fieldValues) {
      data.fieldValues.map(o => {
        form.addControl(o.id, new FormControl(o.value));
      });
    }

    form.addControl('pictures', new FormControl(JSON.stringify(data.pictures)));

    form.addControl('videos', new FormControl(JSON.stringify(data.videos)));

    form.addControl('latitude', new FormControl(data.latitude));
    form.addControl('longitude', new FormControl(data.longitude));
    form.addControl('location', new FormControl(data.location));
    form.addControl('station_range', new FormControl(data.station_range));
    form.addControl('map_feature_link', new FormControl(data.map_feature_link ?? null));

    return form;
  }

  getFormGroup(form, controlName) {
    if (form && form.get(controlName)) {
      return form.get(controlName) as FormGroup;
    }

    return new FormGroup({}) as FormGroup;
  }

  getSelectedPictures() {
    const pictures = this.getFormGroup(this.form, this.selectedMaterialId)?.get('pictures')?.value || '[]';
    return JSON.parse(pictures);
  }

  getSelectedVideos() {
    const videos = this.getFormGroup(this.form, this.selectedMaterialId)?.get('videos')?.value || '[]';
    return JSON.parse(videos);
  }

  selectLineItem(lineItemId) {
    this.selectedMaterialId = this.selectedMaterialId === lineItemId ? this.data.id : lineItemId;

    this.showHeaderDetails = this.selectedMaterialId === this.data.id;

    const tempPictureData = this.getSelectedPictures() ?? [];
    const tempVideosData = this.getSelectedVideos() ?? [];
    this.selectedMediaFiles = [...tempPictureData, ...tempVideosData];

    setTimeout(() => {
      const ele = $(`#${lineItemId}`)[0];

      if (ele) {
        ele.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  }

  deleteLineItem(lineItemId) {
    if (!lineItemId.includes('new_')) {
      this.linesToDelete.push(lineItemId);
    }

    this.form.removeControl(lineItemId);
    this.data.materials = this.data?.materials?.filter(o => o.id !== lineItemId);

    if (this.selectedMaterialId === lineItemId) {
      this.selectLineItem(this.data.id);
    }

    this.updateMaterialsDropdown();
  }

  deletePicture(pic) {
    if (pic.fileType === 'picture') {
      let pictures = this.getSelectedPictures();

      pictures = pictures?.filter(o => o.id !== pic.id);
      this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
      this.getFormGroup(this.form, this.selectedMaterialId)?.get('pictures').setValue(JSON.stringify(pictures));
    }

    if (pic.fileType === 'video') {
      let videos = this.getSelectedVideos();

      videos = videos?.filter(o => o.id !== pic.id);
      this.selectedMediaFiles = this.selectedMediaFiles?.filter(o => o.id !== pic.id);
      this.getFormGroup(this.form, this.selectedMaterialId)?.get('videos').setValue(JSON.stringify(videos));
      this.videoToBeDeletedFromMuxDb.push(pic.id);
    }
  }

  deleteLocation(lineItemId) {
    clearLocationControls(this.getFormGroup(this.form, lineItemId), ['station_range']);
  }

  showLocationSection(lineItemId) {
    const formGroup = this.getFormGroup(this.form, lineItemId);
    const hasGps = (
      this.getFormGroup(this.form, lineItemId)?.controls?.latitude?.value !== null &&
      this.getFormGroup(this.form, lineItemId)?.controls?.longitude?.value !== null &&
      this.getFormGroup(this.form, lineItemId)?.controls?.latitude?.value !== '' &&
      this.getFormGroup(this.form, lineItemId)?.controls?.longitude?.value !== ''
    );

    // W50: a sheet-only drawing link has no pin but still shows its card
    return hasGps || !!formGroup?.controls?.location?.value || !!formGroup?.controls?.station_range?.value
      || !!formGroup?.controls?.map_feature_link?.value?.sheet;
  }

  updateMaterialsDropdown() {
    const selectedMaterials = this.data.materials.map(m => m.initialState.material.id);

    this.materialForm.patchValue({ materials: selectedMaterials }, { emitEvent: false });

    this.materialOptions = this.selectedBidItem.materials
      .filter(o => o.track_received)
      .map(o => ({
        label: `${o.name} (${o.uom})`,
        value: o.id,
        disabled: selectedMaterials.includes(o.id),
      }));
  }

  addMaterialToBidItem(materialId) {
    const selectedMaterial = this.selectedBidItem.materials.find(o => o.id === materialId);

    const fieldValues = this.customFields.map(o => ({
      id: o.id,
      value: null,
    }));

    const id = `new_${new Date().getTime()}`;

    this.data.materials.push({
      id,
      heading: selectedMaterial.name,
      uom: selectedMaterial.uom,
      quantity: null,
      fieldValues,
      latitude: null,
      longitude: null,
      location: null,
      station_range: null,
      map_feature_link: null,
      pictures: [],
      videos: [],
      comment: '',
      type: 'delivery',
      subType: 'onlyMaterial',
      initialState: {
        material: selectedMaterial,
        bid_item_id: this.selectedBidItem.id,
      },
    });

    this.createForm();

    this.updateMaterialsDropdown();

    this.selectLineItem(id);
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
